import { useState, useEffect } from 'react';

/**
 * Hook to preload images and return HTMLImageElement objects
 * This ensures images are fully loaded before rendering, preventing glitches
 */
export const useImagePreloader = (imagePaths: { [key: string]: string }) => {
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: HTMLImageElement }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);

  useEffect(() => {
    const imageEntries = Object.entries(imagePaths);
    const totalImages = imageEntries.length;
    let loadedCount = 0;
    const images: { [key: string]: HTMLImageElement } = {};

    if (totalImages === 0) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadProgress(0);

    imageEntries.forEach(([key, path]) => {
      const img = new Image();

      img.onload = () => {
        images[key] = img;
        loadedCount++;
        setLoadProgress((loadedCount / totalImages) * 100);

        // When all images are loaded, update state
        if (loadedCount === totalImages) {
          setLoadedImages(images);
          setIsLoading(false);
        }
      };

      img.onerror = () => {
        console.error(`Failed to load image: ${path}`);
        loadedCount++;
        setLoadProgress((loadedCount / totalImages) * 100);

        // Continue even if some images fail
        if (loadedCount === totalImages) {
          setLoadedImages(images);
          setIsLoading(false);
        }
      };

      img.src = path;
    });
  }, [imagePaths]);

  return { loadedImages, isLoading, loadProgress };
};
